import { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Alert, Snackbar, CircularProgress, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const UserCreate = () => {
    useSessionValidation();

    const history = useHistory();
    const { isAuthenticated, companyId, branchId, userId, logout, token } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [userNameStatus, setUserNameStatus] = useState(null);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); }
    }, [isAuthenticated, branchId, companyId]);

    const [firstName, setFirstName]         = useState('');
    const [lastName, setLastName]           = useState('');
    const [userName, setUserName]           = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [email, setEmail]                 = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage]     = useState('');

    useEffect(() => {
        const generated = `${firstName.toLowerCase()}_${lastName.toLowerCase()}`;
        const val = generated.slice(0, 20);
        setUserName(val);
        setUserNameStatus(null);
        if (val) checkUserName(val);
    }, [firstName, lastName]);

    const checkUserName = (value) => {
        if (!value?.trim()) { setUserNameStatus(null); return; }
        const existing = JSON.parse(localStorage.getItem('existingUserNames') || '[]');
        setUserNameStatus(existing.includes(value.trim().toLowerCase()) ? 'taken' : 'available');
    };

    const handleUserNameChange = (e) => {
        const val = e.target.value.slice(0, 20);
        setUserName(val);
        checkUserName(val);
    };

    const clearForm = () => {
        setFirstName(''); setLastName(''); setUserName('');
        setContactNumber(''); setEmail('');
    };

    const submitData = async (e) => {
        e.preventDefault();
        if (!firstName)     { setErrorMessage('First Name is required!'); return; }
        if (!lastName)      { setErrorMessage('Last Name is required!'); return; }
        if (!contactNumber) { setErrorMessage('Contact Number is required!'); return; }
        if (!email)         { setErrorMessage('Email is required!'); return; }
        if (userNameStatus === 'taken') { setErrorMessage('Username already taken.'); return; }

        setIsLoading(true);
        const payload = {
            FirstName: firstName, LastName: lastName, UserName: userName,
            Email: email, ContactNumber: contactNumber,
            CreatedBy: userId, CompanyId: companyId, BranchId: branchId,
            StaffId: userId, CreatedDate: new Date(),
        };
        postData('UserApi', '', payload, token)
            .then(() => {
                setSuccessMessage('User created successfully!');
                clearForm();
                history.push('/AuthConsole');
            })
            .catch(() => { setErrorMessage('Failed to create user.'); setIsLoading(false); });
    };

    // ── Shared styles ──
    const label = { display: 'block', fontSize: '10px', fontWeight: '700', color: '#718096', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.4px' };

    const input = (hasError) => ({
        width: '100%', padding: '6px 10px', fontSize: '12px',
        borderRadius: '7px', border: `1px solid ${hasError ? '#e53e3e' : '#d0d7e3'}`,
        background: 'white', outline: 'none', fontFamily: 'Times New Roman',
        boxSizing: 'border-box', color: '#2d3748',
        transition: 'border-color 0.2s',
    });

    const sectionHeader = (icon, title) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px', paddingBottom: '7px', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ background: 'linear-gradient(135deg,#2C3E6E,#3d5491)', borderRadius: '6px', padding: '5px', display: 'flex' }}>
                {icon}
            </div>
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#2C3E6E', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
        </div>
    );

    return (
        <div style={{ minWidth: '100%', background: '#f0f2f5', padding: '10px', fontFamily: 'Times New Roman' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

                {/* ── Gradient Header ── */}
                <div style={{
                    background: 'linear-gradient(135deg, #2C3E6E 0%, #3d5491 100%)',
                    borderRadius: '12px', padding: '10px 16px', marginBottom: '10px',
                    color: 'white', boxShadow: '0 4px 12px rgba(44,62,110,0.25)',
                    display: 'flex', alignItems: 'center', gap: '12px',
                }}>
                    {/* Icon */}
                    <div style={{ background: 'rgba(255,255,255,0.18)', borderRadius: '8px', padding: '7px', display: 'flex', flexShrink: 0 }}>
                        <PersonIcon style={{ fontSize: '18px' }} />
                    </div>

                    {/* Title + subtitle */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: '700', letterSpacing: '0.2px' }}>Create User</div>
                        <div style={{ fontSize: '11px', opacity: 0.82, marginTop: '1px' }}>Add a new user to the system</div>
                    </div>

                    {/* Category pills */}
                    <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                        {[
                            { icon: '👤', label: 'Personal Info' },
                            { icon: '📞', label: 'Contact Details' },
                            { icon: '🔐', label: 'Permissions via Role' },
                        ].map(s => (
                            <span key={s.label} style={{
                                background: 'rgba(255,255,255,0.15)', padding: '3px 9px',
                                borderRadius: '5px', fontSize: '11px', fontWeight: '600',
                                display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                            }}>
                                {s.icon} {s.label}
                            </span>
                        ))}
                    </div>

                    {/* Buttons */}
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <Button
                            variant="contained"
                            onClick={submitData}
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: '13px' }} />}
                            sx={{
                                background: 'rgba(255,255,255,0.22)', color: 'white',
                                padding: '4px 12px', borderRadius: '7px', fontSize: '11px',
                                fontWeight: '700', textTransform: 'none', minHeight: '28px',
                                boxShadow: 'none',
                                '&:hover': { background: 'rgba(255,255,255,0.32)' },
                                '&:disabled': { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' },
                            }}
                        >
                            {isLoading ? 'Saving...' : 'Save User'}
                        </Button>
                        <Button
                            variant="outlined"
                            component={Link}
                            to="/AuthConsole"
                            startIcon={<ArrowBackIcon sx={{ fontSize: '13px' }} />}
                            sx={{
                                borderColor: 'rgba(255,255,255,0.5)', color: 'white',
                                padding: '4px 12px', borderRadius: '7px', fontSize: '11px',
                                fontWeight: '700', textTransform: 'none', minHeight: '28px',
                                '&:hover': { borderColor: 'white', background: 'rgba(255,255,255,0.1)' }
                            }}
                        >
                            Back
                        </Button>
                    </div>
                </div>

                {/* ── Personal Information + Contact in one wide card ── */}
                <div style={{ background: 'white', borderRadius: '10px', padding: '14px 16px', marginBottom: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                    {sectionHeader(<PersonIcon style={{ fontSize: '14px', color: 'white' }} />, 'Personal Information')}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                        <div>
                            <label style={label}>First Name *</label>
                            <input value={firstName} onChange={e => setFirstName(e.target.value)} type="text" placeholder="Enter first name" maxLength={30} style={input(false)} />
                        </div>
                        <div>
                            <label style={label}>Last Name *</label>
                            <input value={lastName} onChange={e => setLastName(e.target.value)} type="text" placeholder="Enter last name" maxLength={30} style={input(false)} />
                        </div>
                        <div>
                            <label style={label}>
                                Username *&nbsp;
                                {userNameStatus === 'available' && <span style={{ color: '#11998e', textTransform: 'none', fontWeight: '700' }}>✓ Available</span>}
                                {userNameStatus === 'taken'     && <span style={{ color: '#e53e3e', textTransform: 'none', fontWeight: '700' }}>✗ Taken</span>}
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    value={userName}
                                    onChange={handleUserNameChange}
                                    type="text"
                                    placeholder="Auto-generated"
                                    maxLength={20}
                                    style={{
                                        ...input(userNameStatus === 'taken'),
                                        paddingRight: '28px',
                                        border: `1px solid ${userNameStatus === 'taken' ? '#e53e3e' : userNameStatus === 'available' ? '#11998e' : '#d0d7e3'}`
                                    }}
                                />
                                <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
                                    {userNameStatus === 'available' && <CheckCircleIcon style={{ fontSize: '14px', color: '#11998e' }} />}
                                    {userNameStatus === 'taken'     && <CancelIcon     style={{ fontSize: '14px', color: '#e53e3e' }} />}
                                </div>
                            </div>
                            <div style={{ fontSize: '10px', color: '#a0aec0', marginTop: '2px' }}>{userName.length}/20</div>
                        </div>
                        <div>
                            <label style={label}>Contact Number *</label>
                            <input value={contactNumber} onChange={e => setContactNumber(e.target.value)} type="text" placeholder="Enter contact number" maxLength={15} style={input(false)} />
                        </div>
                        <div>
                            <label style={label}>Email Address *</label>
                            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="user@example.com" maxLength={40} style={input(false)} />
                        </div>
                    </div>
                </div>

                {/* ── Info note ── */}
                <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '8px', padding: '8px 14px', fontSize: '11px', color: '#1565c0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    ℹ️ After creating the user, open their <strong style={{ margin: '0 3px' }}>Detail</strong> page to assign roles, claims, and branch login permissions.
                </div>

            </div>

            <Snackbar open={!!errorMessage || !!successMessage} autoHideDuration={4000} onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} severity={errorMessage ? 'error' : 'success'} sx={{ fontSize: '12px' }}>
                    {errorMessage || successMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default UserCreate;
